import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@auctisync/ui';
import { useEffect, useState } from 'react';
import { readClaims, useSession } from '../../hooks/useSession';
import { supabase } from '../../lib/supabase';

interface PushCounts {
  total_bidders: number;
  subscribed_bidders: number;
  total_devices: number;
}

/**
 * Post-auth landing for tenant staff. Outer chrome (nav, role badges,
 * sign-out) is provided by AdminChromeLayout.
 */
export function AdminDashboard() {
  const { session } = useSession();
  const claims = readClaims(session);

  return (
    <div>
      <header
        style={{
          marginBottom: '1.5rem',
          padding: '1.5rem 1.75rem',
          borderRadius: 'var(--r-card, 0.75rem)',
          background: 'var(--admin-wash, var(--surface-elev))',
          border: '1px solid var(--admin-hairline, var(--border))',
          boxShadow: 'var(--elev-1)',
        }}
      >
        <h1
          style={{
            fontFamily: 'var(--font-heading-active)',
            fontSize: 'clamp(1.6rem, 1.2rem + 1.6vw, 2rem)',
            fontWeight: 700,
            letterSpacing: '-0.02em',
            color: 'var(--text)',
            marginBottom: '0.25rem',
          }}
        >
          Admin dashboard
        </h1>
        <p style={{ color: 'var(--text-muted)' }}>
          Signed in as {session?.user.email ?? 'unknown'}
        </p>
      </header>

      <div style={{ display: 'grid', gap: '1rem' }}>
        <PushSubscribersCard tenantId={claims.tenantId} />

        <Card>
          <CardHeader>
            <CardTitle>Module #3 scaffold</CardTitle>
            <CardDescription>
              Inventory, Taxonomies, and Consignors sections are wired up. Pick any from the
              sidebar.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
              List views, detail/edit forms, photo upload, and bulk actions land in the following
              steps of Module #3.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function PushSubscribersCard({ tenantId }: { tenantId: string | null }) {
  const [counts, setCounts] = useState<PushCounts | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    void (async () => {
      const { data, error: err } = await supabase.rpc('tenant_push_subscriber_counts', {
        p_tenant_id: tenantId,
      });
      if (cancelled) return;
      if (err) {
        setError(err.message);
      } else if (data) {
        setCounts(data as unknown as PushCounts);
      }
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  if (!tenantId) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Push subscribers</CardTitle>
        <CardDescription>
          How many of your bidders have push notifications enabled, across how many devices. Raw
          push tokens are never exposed.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading && <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Loading…</p>}
        {error && (
          <p style={{ color: 'var(--text-danger, #991b1b)', fontSize: '0.875rem' }}>{error}</p>
        )}
        {counts && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(10rem, 1fr))',
              gap: '0.75rem',
            }}
          >
            <Metric label="Bidders" value={counts.total_bidders} />
            <Metric
              label="Subscribed bidders"
              value={counts.subscribed_bidders}
              hint={
                counts.total_bidders > 0
                  ? `${Math.round((counts.subscribed_bidders / counts.total_bidders) * 100)}% of bidders`
                  : undefined
              }
            />
            <Metric label="Active devices" value={counts.total_devices} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Metric({
  label,
  value,
  hint,
}: {
  label: string;
  value: number;
  hint?: string | undefined;
}) {
  return (
    <div
      style={{
        padding: '0.85rem 1rem',
        border: '1px solid var(--admin-hairline, var(--border))',
        borderRadius: 'var(--r-tile, 0.375rem)',
        background: 'var(--surface-elev)',
        boxShadow: 'var(--elev-1)',
      }}
    >
      <div
        style={{
          fontSize: '0.75rem',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: 'var(--text-muted)',
          marginBottom: '0.25rem',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: '1.75rem',
          fontWeight: 700,
          color: 'var(--text)',
          lineHeight: 1.1,
        }}
      >
        {value}
      </div>
      {hint && (
        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
          {hint}
        </div>
      )}
    </div>
  );
}
